import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload,
  Sparkles,
  Plus,
  Trash2,
  ArrowRight,
  ArrowLeft,
  FileText,
  CalendarClock,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

const STEPS = ["Upload & extract", "Confirm constraints", "Students & services", "Generated schedule"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

export default function Schedule() {
  const [step, setStep] = useState(0);
  const [docs, setDocs] = useState([]); // { filename, text, parsed }
  const [extracting, setExtracting] = useState(false);
  const [combinedConstraints, setCombinedConstraints] = useState("");
  const [blocks, setBlocks] = useState([]); // list of time blocks
  const [missingInfo, setMissingInfo] = useState([]);
  const [services, setServices] = useState([]);
  const [students, setStudents] = useState([]);
  const [teacherName, setTeacherName] = useState("");
  const [teacherAvail, setTeacherAvail] = useState("");
  const [building, setBuilding] = useState(false);
  const [result, setResult] = useState(null);
  const [savedSchedules, setSavedSchedules] = useState([]);
  const [pasteText, setPasteText] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data));
    api.get("/schedule").then((r) => setSavedSchedules(r.data));
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/schedule/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = await api.post("/schedule/extract", { text: up.data.text, doc_name: up.data.filename });
      if (!ex.data.parsed) {
        toast.error("AI couldn't parse this document. Try another file or paste text.");
        return;
      }
      const newDoc = { filename: up.data.filename, text: up.data.text, parsed: ex.data.parsed };
      setDocs((d) => [...d, newDoc]);
      mergeFromParsed(ex.data.parsed);
      toast.success(`Extracted ${ex.data.parsed.time_blocks?.length || 0} time blocks from ${file.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not extract document");
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handlePasteExtract = async () => {
    if (!pasteText.trim()) {
      toast.error("Paste some schedule text first");
      return;
    }
    setExtracting(true);
    try {
      const ex = await api.post("/schedule/extract", { text: pasteText, doc_name: "pasted text" });
      if (!ex.data.parsed) {
        toast.error("AI couldn't parse the text. Try adding more context.");
        return;
      }
      const newDoc = { filename: "pasted text", text: pasteText, parsed: ex.data.parsed };
      setDocs((d) => [...d, newDoc]);
      mergeFromParsed(ex.data.parsed);
      setPasteText("");
      toast.success("Constraints extracted");
    } finally {
      setExtracting(false);
    }
  };

  const mergeFromParsed = (parsed) => {
    setBlocks((b) => [...b, ...(parsed.time_blocks || [])]);
    setMissingInfo((m) => [...m, ...(parsed.missing_information || [])]);
    setCombinedConstraints((c) => (c ? c + "\n\n" : "") + (parsed.summary || ""));
  };

  const removeDoc = (idx) => {
    setDocs((d) => d.filter((_, i) => i !== idx));
  };

  const addService = (student) => {
    setServices((s) => [
      ...s,
      {
        student_id: student?.id || null,
        student_name: student ? `${student.first_name} ${student.last_name}` : "",
        service_type: "",
        minutes_per_week: 150,
        sessions_per_week: 5,
        group_size: "1:1",
        provider: teacherName || "",
        preferred_location: "",
        notes: "",
      },
    ]);
  };

  const updateService = (idx, key, val) => {
    setServices((s) => s.map((row, i) => (i === idx ? { ...row, [key]: val } : row)));
  };

  const removeService = (idx) => setServices((s) => s.filter((_, i) => i !== idx));

  const build = async () => {
    if (services.length === 0) return toast.error("Add at least one service to schedule");
    if (services.some((s) => !s.student_name || !s.service_type)) return toast.error("Every row needs a student name and service type");
    setBuilding(true);
    try {
      const r = await api.post("/schedule/build", {
        constraints_summary: combinedConstraints,
        raw_blocks: blocks,
        services,
        teacher_name: teacherName,
        teacher_availability: teacherAvail,
        week_pattern: "Mon-Fri",
      });
      if (!r.data.parsed) {
        toast.error("AI returned an unstructured response. Try shortening constraints or simplifying services.");
        return;
      }
      setResult(r.data.parsed);
      setStep(3);
    } catch (e) {
      toast.error("Could not build schedule");
    } finally {
      setBuilding(false);
    }
  };

  const saveSchedule = async () => {
    if (!result) return;
    const name = window.prompt("Name this schedule:", `Service Schedule ${new Date().toLocaleDateString()}`);
    if (!name) return;
    try {
      await api.post("/schedule/save", {
        name,
        summary: result.summary || "",
        constraints: combinedConstraints,
        proposed: result.proposed || {},
        conflicts: result.conflicts || [],
        alternates: result.alternates || [],
        services,
      });
      toast.success("Schedule saved");
      const r = await api.get("/schedule");
      setSavedSchedules(r.data);
    } catch (e) {
      toast.error("Could not save schedule");
    }
  };

  const reset = () => {
    setStep(0);
    setDocs([]);
    setBlocks([]);
    setMissingInfo([]);
    setServices([]);
    setCombinedConstraints("");
    setTeacherName("");
    setTeacherAvail("");
    setResult(null);
    setPasteText("");
  };

  const openSaved = async (id) => {
    const r = await api.get(`/schedule/${id}`);
    setResult({
      proposed: r.data.proposed,
      conflicts: r.data.conflicts,
      alternates: r.data.alternates,
      summary: r.data.summary,
    });
    setCombinedConstraints(r.data.constraints || "");
    setServices(r.data.services || []);
    setStep(3);
  };

  const deleteSaved = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    await api.delete(`/schedule/${id}`);
    const r = await api.get("/schedule");
    setSavedSchedules(r.data);
  };

  return (
    <div className="space-y-8" data-testid="schedule-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline">Instructional schedule assistant</div>
          <h1 className="font-heading text-4xl font-medium ink mt-3">Build a service schedule</h1>
          <p className="ink-muted mt-2 max-w-2xl">
            Upload your master schedule, district calendar, and any campus events. The assistant extracts time blocks,
            flags conflicts, and proposes a clear weekly schedule honoring each student's IEP minutes and group size.
          </p>
        </div>
        {step > 0 && (
          <Button variant="ghost" onClick={reset} data-testid="schedule-reset">
            Start over
          </Button>
        )}
      </header>

      {/* Stepper */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="schedule-stepper">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${i <= step ? "bg-[#6E5AA0] text-white" : "bg-[#F0EBF6] text-[#6B726F]"}`}>
              {i + 1}
            </div>
            <span className={`text-sm ${i === step ? "ink font-medium" : "ink-muted"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="w-6 h-px bg-[#E8E9E6] mx-1" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <Card className="p-6 sm:p-8 border border-[#E8E9E6] bg-white" data-testid="schedule-upload-step">
          <div className="grid lg:grid-cols-2 gap-6">
            <div>
              <h3 className="font-heading text-xl font-medium ink">Upload schedule documents</h3>
              <p className="ink-muted text-sm mt-1">
                PDF, DOCX, or TXT. Add your school master schedule, district calendar, and any campus event lists.
              </p>
              <div className="mt-5 border-2 border-dashed border-[#D8D9D5] rounded-2xl p-8 text-center bg-[#FBFBF9]">
                <Upload className="mx-auto text-[#6E5AA0]" size={28} />
                <p className="ink mt-3 font-medium">Drop files or click to upload</p>
                <p className="ink-muted text-xs mt-1">PDF · DOCX · TXT (max 10 MB)</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.docx,.txt,.csv,.md"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="schedule-file-input"
                />
                <Button
                  className="mt-4 bg-[#6E5AA0] hover:bg-[#574785] text-white"
                  onClick={() => fileRef.current?.click()}
                  disabled={extracting}
                  data-testid="schedule-pick-file-button"
                >
                  {extracting ? "Extracting…" : "Choose file"}
                </Button>
              </div>

              <div className="mt-6 space-y-2">
                <div className="overline">Or paste schedule text</div>
                <Textarea
                  rows={5}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="Paste the schedule directly here…"
                  data-testid="schedule-paste-area"
                />
                <Button variant="outline" onClick={handlePasteExtract} disabled={extracting} data-testid="schedule-paste-extract">
                  <Sparkles size={14} className="mr-2" /> Extract from text
                </Button>
              </div>
            </div>

            <div>
              <h3 className="font-heading text-xl font-medium ink">Documents added</h3>
              <p className="ink-muted text-sm mt-1">Each document is analyzed by AI to extract time blocks and constraints.</p>
              <div className="mt-5 space-y-3">
                {docs.length === 0 && (
                  <div className="text-sm ink-muted">No documents yet.</div>
                )}
                {docs.map((d, i) => (
                  <Card key={i} className="p-4 border border-[#E8E9E6] bg-white" data-testid={`schedule-doc-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <FileText size={16} className="text-[#6E5AA0] shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <div className="font-medium ink truncate">{d.filename}</div>
                          <div className="text-xs ink-muted">
                            {d.parsed?.doc_type || "document"} · {d.parsed?.time_blocks?.length || 0} blocks
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeDoc(i)} className="text-[#BA6356] shrink-0">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={() => setStep(1)}
                  disabled={docs.length === 0}
                  className="bg-[#6E5AA0] hover:bg-[#574785] text-white"
                  data-testid="schedule-step-next-0"
                >
                  Continue <ArrowRight size={14} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {savedSchedules.length > 0 && (
            <div className="mt-10 pt-6 border-t border-[#E8E9E6]">
              <h3 className="font-heading text-xl font-medium ink">Saved schedules</h3>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedSchedules.map((s) => (
                  <Card key={s.id} className="p-4 border border-[#E8E9E6] bg-white" data-testid={`saved-schedule-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <button onClick={() => openSaved(s.id)} className="text-left flex-1">
                        <div className="font-medium ink">{s.name}</div>
                        <div className="text-xs ink-muted mt-1">{new Date(s.created_at).toLocaleString()}</div>
                        <div className="text-xs ink-muted mt-1">
                          {s.proposed?.rows?.length || 0} sessions · {s.conflicts?.length || 0} conflicts
                        </div>
                      </button>
                      <Button variant="ghost" size="sm" onClick={() => deleteSaved(s.id)} className="text-[#BA6356]">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {step === 1 && (
        <Card className="p-6 sm:p-8 border border-[#E8E9E6] bg-white" data-testid="schedule-constraints-step">
          <h3 className="font-heading text-xl font-medium ink">Confirm constraints</h3>
          <p className="ink-muted text-sm mt-1">Review what the AI pulled out. Edit the summary if anything is wrong or missing.</p>

          <div className="grid lg:grid-cols-5 gap-6 mt-6">
            <div className="lg:col-span-3 space-y-3">
              <Label>Plain-English summary (sent to the scheduler)</Label>
              <Textarea
                rows={10}
                value={combinedConstraints}
                onChange={(e) => setCombinedConstraints(e.target.value)}
                data-testid="schedule-constraints-text"
              />
              {missingInfo.length > 0 && (
                <div className="border border-[#E8E9E6] bg-[#FBFBF9] rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={14} className="text-[#D4A373]" />
                    <span className="font-medium ink text-sm">Missing or unclear info</span>
                  </div>
                  <ul className="space-y-1 list-disc list-inside ink-muted text-sm">
                    {missingInfo.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}
            </div>

            <div className="lg:col-span-2 space-y-2 max-h-[28rem] overflow-y-auto">
              <Label className="overline">Time blocks ({blocks.length})</Label>
              {blocks.length === 0 && <div className="text-sm ink-muted">No blocks yet.</div>}
              {blocks.map((b, i) => (
                <div key={i} className="border border-[#EDEDE9] rounded-lg p-3 text-xs bg-[#FBFBF9]" data-testid={`block-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium ink">{b.day} · {b.start}–{b.end}</div>
                    <Badge variant="outline" className="bg-white text-[10px]">{b.kind}</Badge>
                  </div>
                  <div className="ink-muted mt-1">{b.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <Button variant="ghost" onClick={() => setStep(0)} data-testid="schedule-back-0">
              <ArrowLeft size={14} className="mr-2" /> Back
            </Button>
            <Button onClick={() => setStep(2)} disabled={!combinedConstraints.trim()} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="schedule-step-next-1">
              Continue <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="p-6 sm:p-8 border border-[#E8E9E6] bg-white" data-testid="schedule-services-step">
          <h3 className="font-heading text-xl font-medium ink">Students, services, and availability</h3>
          <p className="ink-muted text-sm mt-1">Add a row per service. Pull students from your roster or type in any name.</p>

          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <div className="space-y-2">
              <Label>Provider / teacher name</Label>
              <Input value={teacherName} onChange={(e) => setTeacherName(e.target.value)} placeholder="Ms. Garcia" data-testid="teacher-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Teacher availability (free text)</Label>
              <Input value={teacherAvail} onChange={(e) => setTeacherAvail(e.target.value)} placeholder="e.g., Mon-Fri 8:00-3:30, no pulls Wed 12:30-1:30 (PLC)" data-testid="teacher-avail-input" />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <div className="font-medium ink">Services to schedule</div>
            <div className="flex gap-2">
              <Select onValueChange={(v) => {
                const s = students.find((x) => x.id === v);
                addService(s);
              }}>
                <SelectTrigger className="w-56" data-testid="add-from-roster">
                  <SelectValue placeholder="Add from roster…" />
                </SelectTrigger>
                <SelectContent>
                  {students.length === 0 && <div className="px-2 py-1 text-xs ink-muted">No students yet</div>}
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · Grade {s.grade}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => addService(null)} data-testid="add-blank-service">
                <Plus size={14} className="mr-2" /> Add row
              </Button>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {services.length === 0 && (
              <div className="border border-dashed border-[#D8D9D5] rounded-xl p-6 text-sm ink-muted text-center">
                No services yet. Add a row to start scheduling.
              </div>
            )}
            {services.map((row, i) => (
              <Card key={i} className="p-4 border border-[#E8E9E6] bg-[#FBFBF9]" data-testid={`service-row-${i}`}>
                <div className="grid sm:grid-cols-12 gap-3 items-end">
                  <div className="sm:col-span-3 space-y-1.5">
                    <Label className="text-xs">Student</Label>
                    <Input value={row.student_name} onChange={(e) => updateService(i, "student_name", e.target.value)} data-testid={`service-student-${i}`} />
                  </div>
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label className="text-xs">Service type</Label>
                    <Input value={row.service_type} onChange={(e) => updateService(i, "service_type", e.target.value)} placeholder="Reading, Math, Speech…" data-testid={`service-type-${i}`} />
                  </div>
                  <div className="sm:col-span-1 space-y-1.5">
                    <Label className="text-xs">Min/wk</Label>
                    <Input type="number" value={row.minutes_per_week} onChange={(e) => updateService(i, "minutes_per_week", parseInt(e.target.value || 0))} data-testid={`service-min-${i}`} />
                  </div>
                  <div className="sm:col-span-1 space-y-1.5">
                    <Label className="text-xs">Sess/wk</Label>
                    <Input type="number" value={row.sessions_per_week || ""} onChange={(e) => updateService(i, "sessions_per_week", parseInt(e.target.value || 0))} data-testid={`service-sess-${i}`} />
                  </div>
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label className="text-xs">Group</Label>
                    <Select value={row.group_size} onValueChange={(v) => updateService(i, "group_size", v)}>
                      <SelectTrigger data-testid={`service-group-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1:1">1:1</SelectItem>
                        <SelectItem value="small_group">Small group</SelectItem>
                        <SelectItem value="large_group">Large group</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label className="text-xs">Location</Label>
                    <Input value={row.preferred_location || ""} onChange={(e) => updateService(i, "preferred_location", e.target.value)} placeholder="Resource room…" data-testid={`service-loc-${i}`} />
                  </div>
                  <div className="sm:col-span-1 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => removeService(i)} className="text-[#BA6356]">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <div className="sm:col-span-12 space-y-1.5">
                    <Label className="text-xs">Notes (optional)</Label>
                    <Input value={row.notes || ""} onChange={(e) => updateService(i, "notes", e.target.value)} placeholder="e.g., 'prefers mornings', 'pull-out only'" />
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="mt-6 flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)} data-testid="schedule-back-1">
              <ArrowLeft size={14} className="mr-2" /> Back
            </Button>
            <Button onClick={build} disabled={building || services.length === 0} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="schedule-build-button">
              {building ? <span className="calm-pulse">Building schedule…</span> : <><Sparkles size={14} className="mr-2" /> Build schedule</>}
            </Button>
          </div>
        </Card>
      )}

      {step === 3 && result && (
        <ResultView
          result={result}
          onSave={saveSchedule}
          onBack={() => setStep(2)}
        />
      )}
    </div>
  );
}

function ResultView({ result, onSave, onBack }) {
  return (
    <div className="space-y-6" data-testid="schedule-result">
      <Card className="p-6 sm:p-8 border border-[#E8E9E6] bg-white">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="overline">Summary</div>
            <p className="ink mt-2">{result.summary}</p>
          </div>
          <Button onClick={onSave} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="schedule-save-button">
            <CheckCircle2 size={14} className="mr-2" /> Save schedule
          </Button>
        </div>
      </Card>

      <ScheduleTable proposed={result.proposed} testid="proposed-table" />

      {result.weekly_minutes_check?.length > 0 && (
        <Card className="p-6 border border-[#E8E9E6] bg-white">
          <div className="font-heading text-lg font-medium ink mb-3">Weekly minutes check</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[#E8E9E6]">
                  <th className="py-2 pr-4 ink-muted font-medium">Student</th>
                  <th className="py-2 pr-4 ink-muted font-medium">Service</th>
                  <th className="py-2 pr-4 ink-muted font-medium">Required</th>
                  <th className="py-2 pr-4 ink-muted font-medium">Scheduled</th>
                  <th className="py-2 pr-4 ink-muted font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {result.weekly_minutes_check.map((c, i) => (
                  <tr key={i} className="border-b border-[#EDEDE9] last:border-0">
                    <td className="py-2 pr-4 ink">{c.student}</td>
                    <td className="py-2 pr-4 ink-muted">{c.service_type}</td>
                    <td className="py-2 pr-4 ink-muted">{c.required} min</td>
                    <td className="py-2 pr-4 ink">{c.scheduled} min</td>
                    <td className="py-2 pr-4">
                      <Badge className={
                        c.status === "met" ? "bg-[#E1E9DE] text-[#3C5C40] hover:bg-[#E1E9DE]"
                          : c.status === "over" ? "bg-[#F3E6D4] text-[#7C5B25] hover:bg-[#F3E6D4]"
                          : "bg-[#F5DBD6] text-[#7C2D1D] hover:bg-[#F5DBD6]"
                      }>{c.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {result.conflicts?.length > 0 && (
        <Card className="p-6 border border-[#E8E9E6] bg-white">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-[#BA6356]" />
            <div className="font-heading text-lg font-medium ink">Conflicts ({result.conflicts.length})</div>
          </div>
          <div className="space-y-3">
            {result.conflicts.map((c, i) => (
              <div key={i} className="border border-[#EDEDE9] rounded-lg p-4 bg-[#FBFBF9]" data-testid={`conflict-${i}`}>
                <div className="font-medium ink">{c.what}</div>
                <div className="text-sm ink-muted mt-1">{c.why}</div>
                <div className="text-sm text-[#6E5AA0] mt-2"><span className="font-semibold">Suggested fix:</span> {c.suggested_fix}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {result.missing_information?.length > 0 && (
        <Card className="p-6 border border-[#E8E9E6] bg-[#FBFBF9]">
          <div className="font-heading text-lg font-medium ink mb-2">The assistant assumed / needs from you</div>
          <ul className="space-y-1 list-disc list-inside ink-muted text-sm">
            {result.missing_information.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </Card>
      )}

      {result.alternates?.length > 0 && (
        <div className="space-y-4">
          <div className="font-heading text-xl font-medium ink">Alternate options</div>
          {result.alternates.map((alt, i) => (
            <Card key={i} className="p-6 border border-[#E8E9E6] bg-white" data-testid={`alternate-${i}`}>
              <div className="font-medium ink">{alt.label}</div>
              <p className="text-sm ink-muted mt-1">{alt.summary}</p>
              <div className="mt-4">
                <ScheduleTable proposed={{ days: DAYS, rows: alt.rows || [] }} testid={`alternate-table-${i}`} compact />
              </div>
            </Card>
          ))}
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft size={14} className="mr-2" /> Back to services
        </Button>
        <Button onClick={onSave} className="bg-[#6E5AA0] hover:bg-[#574785] text-white">
          <CheckCircle2 size={14} className="mr-2" /> Save schedule
        </Button>
      </div>
    </div>
  );
}

function ScheduleTable({ proposed, testid, compact }) {
  const rows = proposed?.rows || [];
  const days = proposed?.days?.length ? proposed.days : DAYS;
  // group rows by day
  const byDay = {};
  days.forEach((d) => (byDay[d] = []));
  rows.forEach((r) => {
    if (!byDay[r.day]) byDay[r.day] = [];
    byDay[r.day].push(r);
  });
  Object.values(byDay).forEach((arr) => arr.sort((a, b) => (a.start || "").localeCompare(b.start || "")));

  return (
    <Card className="p-0 border border-[#E8E9E6] bg-white overflow-hidden" data-testid={testid}>
      {!compact && (
        <div className="px-6 pt-6 pb-3 flex items-center gap-2">
          <CalendarClock size={18} className="text-[#6E5AA0]" />
          <div className="font-heading text-xl font-medium ink">Proposed weekly schedule</div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EFEAF5]">
            <tr className="text-left">
              <th className="py-3 px-4 ink-muted font-medium uppercase tracking-wider text-xs">Day</th>
              <th className="py-3 px-4 ink-muted font-medium uppercase tracking-wider text-xs">Time</th>
              <th className="py-3 px-4 ink-muted font-medium uppercase tracking-wider text-xs">Students</th>
              <th className="py-3 px-4 ink-muted font-medium uppercase tracking-wider text-xs">Service</th>
              <th className="py-3 px-4 ink-muted font-medium uppercase tracking-wider text-xs">Location</th>
              <th className="py-3 px-4 ink-muted font-medium uppercase tracking-wider text-xs">Provider</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center ink-muted">No sessions in this schedule.</td>
              </tr>
            )}
            {days.flatMap((d) =>
              (byDay[d] || []).map((r, i) => (
                <tr key={`${d}-${i}`} className="border-t border-[#EDEDE9]" data-testid={`row-${d}-${i}`}>
                  <td className="py-3 px-4 align-top">
                    {i === 0 && <Badge variant="outline" className="bg-[#EFEAF5]">{d}</Badge>}
                  </td>
                  <td className="py-3 px-4 ink whitespace-nowrap">{r.start} – {r.end}<div className="text-xs ink-muted">{r.minutes} min</div></td>
                  <td className="py-3 px-4 ink">{(r.students || []).join(", ")}</td>
                  <td className="py-3 px-4 ink">{r.service_type}</td>
                  <td className="py-3 px-4 ink-muted">{r.location}</td>
                  <td className="py-3 px-4 ink-muted">{r.provider}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
